<template>
  <div id="cvcSurvey">
    <el-card
      v-loading="loadingWrap"
      :style="{width: dialogWidth}"
      shadow="hover"
      :body-style="{padding: dialogPadding}"
    >
      <!-- Index Cover -->
      <div
        v-if="cover"
        id="cover"
        class="cover"
        :class="['mobile', 'tablet'].includes($mq) ? 'mobileMode' : 'deskMode'"
      >
        <div class="coverWrap">
          <div class="title">
            {{ $t('cvc.CVCReview') }}
          </div>

          <div class="description">
            <div
              v-if="pulseData.coach"
              class="coachInfo"
            >
              {{ $t('coach.coach') }}:
              <b>{{ pulseData.coach.first_name }} {{ pulseData.coach.last_name }}</b>
            </div>
            <br />
            {{ $t('cvc.survey.helpUs') }}
            <br />
            <br />
            {{ $t('cvc.survey.willTake') }}
          </div>

          <!-- Duplicated image source for responsive design -->
          <div
            v-if="['mobile', 'tablet'].includes($mq)"
            class="pulseIntro"
          >
            <img
              src="@/assets/media/pulse_intro.png"
              alt="Pulse intro"
            />
          </div>

          <div class="action">
            <el-button
              type="primary"
              round
              :disabled="!cover"
              @click="sceneChange('cover')"
            >
              {{ $t('cvc.survey.getStarted') }}
              <i class="buttonArrow el-icon-right"></i>
            </el-button>
          </div>
        </div>

        <!-- Duplicated image source for responsive design -->
        <div
          v-if="['desktop', 'laptop'].includes($mq)"
          class="pulseIntro"
        >
          <img
            src="@/assets/media/pulse_intro.png"
            alt="Pulse intro"
          />
        </div>
      </div>

      <!-- Survey Card -->
      <div
        v-if="!cover"
        v-show="!submit"
        id="scene"
        class="scene"
      >
        <!-- Survey Page -->
        <cvc-page
          :pulse-data="pulseData"
          :scene.sync="scene"
          :submit.sync="submit"
          :page-structure="pageStructure"
          :page-source="pageSource"
          :loading="loading"
          @validationPulse="validationPulse"
          @submitPulse="submitPulse"
        />
      </div>

      <!-- Submit question -->
      <div
        v-if="submit"
        id="submit"
        class="submit"
      >
        <div class="description">
          {{ $t('cvc.survey.areYouSure') }}
        </div>

        <div class="action">
          <el-row
            type="flex"
            :gutter="5"
          >
            <el-col :span="responsiveCol">
              <el-button
                type="primary"
                round
                @click="submitPulse"
              >
                {{ $t('common.submit') }}
              </el-button>
            </el-col>
            <el-col :span="responsiveCol">
              <el-button
                type="info"
                round
                @click="submit = false"
              >
                {{ $t('common.goBack') }}
              </el-button>
            </el-col>
          </el-row>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import api from '@/api';
import cvcPage from './CvcPage.vue';
import pageStructure from './data';

export default {
  components: {
    cvcPage,
  },

  props: {
    loadingWrap: {
      type: Boolean,
      default: false,
    },
    role: {
      type: String,
      default: null,
    },
    pulseData: {
      type: Object,
      default: null,
    },
    error: {
      type: Boolean,
      default: false,
    },
    errorData: {
      type: Object,
      default: null,
    },
    confirmation: {
      type: Boolean,
      default: false,
    },
    confirmedData: {
      type: Object,
      default: null,
    },
    dialogWidth: {
      type: String,
      default: '50%',
    },
    dialogPadding: {
      type: String,
      default: '20px',
    },
  },

  data() {
    return {
      loading: false,
      // Sub card flag
      cover: true,
      submit: false,
      // Page data
      scene: 1,
      // All questions data
      pageStructure,
      pageSource: {
        // Unanswered first page
        blank: 1,
        blankList: [],
        firstTry: true,
        // Select Step key, value
        step: {
          // CVC Steps data
          cvc: [
            {
              score: -2,
              desc: this.$t('cvc.score_1'),
            },
            {
              score: -1,
              desc: this.$t('cvc.score_2'),
            },
            {
              score: 0,
              desc: this.$t('cvc.score_3'),
            },
            {
              score: 1,
              desc: this.$t('cvc.score_4'),
            },
            {
              score: 2,
              desc: this.$t('cvc.score_5'),
            },
          ],
        },
        // Answer results
        answer: {},
      },
    };
  },

  computed: {
    responsiveCol() {
      let col = 12;
      if (['mobile', 'tablet'].includes(this.$mq)) {
        col = 24;
      }
      return col;
    },

    questionPageCheck() {
      const pages = {};
      this.pageStructure.forEach((data) => {
        pages[`page${data.page}`] = { type: data.type, list: [] };
        data.list.forEach((el) => {
          if (el.number && el.number !== 'submit') {
            pages[`page${data.page}`].list.push(el.number);
          }
        });
      });
      return pages;
    },
  },

  mounted() {
    this.initAnswer();
  },

  methods: {
    initAnswer() {
      const initAnswerObject = {};
      this.pageStructure.forEach((page) => {
        page.list.forEach((question) => {
          if (question.number && question.number !== 'allow_share' && question.number !== 'submit') {
            if (page.type === 'step') {
              initAnswerObject[`q${question.number}`] = null;
            } else if (page.type === 'textarea') {
              initAnswerObject[`q${question.number}`] = '';
            } else if (page.type === 'flag') {
              initAnswerObject[`q${question.number}`] = null;
            }
          } else if (question.number === 'allow_share') {
            initAnswerObject[`${question.number}`] = null;
          }
        });
      });
      this.pageSource.answer = initAnswerObject;
    },

    sceneChange(option) {
      if (option === 'cover') {
        this.cover = false;
      }
    },

    validationPulse() {
      const obj = this.pageSource.answer;
      const remain = {
        flag: true,
        list: [],
      };
      const blankList = [];

      Object.keys(this.questionPageCheck).forEach((check) => {
        if (this.questionPageCheck[check].type === 'step') {
          this.questionPageCheck[check].list.forEach((number) => {
            if (typeof obj[`q${number}`] !== 'number') {
              blankList.push(
                parseInt(check.replace('page', ''), 10),
              );
              remain.flag = false;
              remain.list.push(`q${number}`);
            }
          });
        } else if (this.questionPageCheck[check].type === 'flag') {
          this.questionPageCheck[check].list.forEach((number) => {
            if (typeof obj[number] !== 'boolean') {
              blankList.push(
                parseInt(check.replace('page', ''), 10),
              );
              remain.flag = false;
              remain.list.push(number);
            }
          });
        }
      });

      this.pageSource.blankList = remain.list;
      this.blank = Math.min(...blankList);
      return remain;
    },

    async submitPulse() {
      try {
        this.loading = true;
        this.pageSource.firstTry = false;
        const flag = await this.validationPulse();

        if (flag.flag) {
          this.sendPulse();
        } else {
          this.$alert(this.$t('cvc.survey.pleaseAnswer'), '', {
            type: 'warning',
            confirmButtonText: this.$t('cvc.survey.goToQ'),
            customClass: 'surveyMessage',
          })
            .then(() => {
              this.scene = this.blank;
              this.submit = false;
            });
        }
      } catch (err) {
        console.log(err);
        this.loading = false;
        if (err.msg) {
          this.$message.error(err.msg);
          this.$emit('update:errorData.text', err.msg);
        }
        this.$emit('update:error', true);
      }
    },

    async sendPulse() {
      try {
        this.loading = true;

        const data = {};

        Object.keys(this.pageSource.answer).forEach((key) => {
          if (key === 'allow_share') {
            data.allow_share = this.pageSource.answer[key];
          } else {
            data[`question${key.split('q')[1]}`] = this.pageSource.answer[key];
          }
        });

        await api.publishEngagementCvcSurvey(this.$route.params.id, data);

        this.$emit('update:confirmation', true);
      } catch (err) {
        if (err.code === 406) {
          this.$emit('update:errorData.text', this.$t('cvc.survey.alreadySubmitted'));
          this.$emit('update:errorData.code', 406);
          this.$emit('update:error', true);
        } else if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#cvcSurvey {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  .el-button--primary {
    background: #00ace2;
    border-color: #00ace2;
  }

  .el-card {
    margin: 0 auto;
    text-align: left;
    overflow: auto;
    background-color: rgba(255, 255, 255, 0.9);
    max-width: 1000px;

    #cover {
      text-align: center;

      .title {
        font-weight: bold;
        font-size: 24px;
        margin-bottom: 15px;
      }

      .description {
        margin-bottom: 30px;
      }

      .pulseIntro {
        width: 30%;
        max-width: 200px;
        min-width: 150px;
        margin: 0 auto;

        img {
          width: 100%;
        }
      }

      .action {
        text-align: center;
        margin-top: 30px;

        .el-button {
          width: 50%;
          min-width: 200px;
          padding: 13px 0;

          .buttonArrow {
            font-size: 14px;
            font-weight: bold;
            color: white;
          }
        }
      }

      &.deskMode {
        text-align: left;
        display: flex;
        align-items: center;

        .coverWrap {
          width: 60%;
        }

        .action {
          text-align: left;

          .el-button {
            width: 70%;
          }
        }
      }
    }

    #submit {
      padding: 25px 0 10px;

      .description {
        text-align: center;
        margin-bottom: 50px;
      }

      .el-row {
        flex-wrap: wrap;

        .el-col {
          padding: 5px 40px !important;

          .el-button {
            width: 100%;
          }
        }
      }
    }

    .lga {
      margin-bottom: 15px;

      .lgaName {
        font-weight: bold;
      }

      .el-textarea__inner {
        box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
      }
    }
  }
}
</style>

<style lang="scss">
  .surveyMessage {
    .el-message-box__btns {
      button {
        min-width: 180px;
      }
    }
  }
</style>
